import React, { useState, useEffect, useCallback } from 'react';
import { Send, Mail, Users, FileText, Loader2, Eye, AlertCircle, RefreshCw, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const SEGMENT_LABELS = {
  all: 'All users',
  paid: 'Paid subscribers',
  trial: 'Trial users',
  expired: 'Expired trials',
  cancelled: 'Cancelled subscribers',
  inactive_30d: 'Inactive 30+ days',
};

const statusBadge = (status) => {
  const map = {
    sending: { cls: 'bg-blue-900/30 text-blue-200', label: 'Sending' },
    sent: { cls: 'bg-emerald-900/30 text-emerald-200', label: 'Sent' },
    failed: { cls: 'bg-amber-900/30 text-amber-200', label: 'Failed' },
  };
  const s = map[status] || { cls: 'bg-[#131C3A] text-slate-100', label: status };
  return <Badge className={s.cls}>{s.label}</Badge>;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const BroadcastsPage = () => {
  const [segments, setSegments] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [segment, setSegment] = useState('all');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refreshAll = async () => {
    try {
      setLoading(true);
      const [segRes, hRes] = await Promise.all([
        api.get('/admin/broadcasts/segments'),
        api.get('/admin/broadcasts?limit=20'),
      ]);
      setSegments(segRes.data.segments || {});
      setHistory(hRes.data.items || []);
    } catch (e) {
      toast.error('Failed to load broadcast data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshAll(); }, []);

  const previewSegment = useCallback(async () => {
    try {
      setPreviewLoading(true);
      const r = await api.post('/admin/broadcasts/preview', { segment });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [segment]);

  useEffect(() => { previewSegment(); }, [previewSegment]);

  const sendTestToMyself = async () => {
    if (!subject || !body) { toast.error('Subject and body are required'); return; }
    try {
      setSending(true);
      const r = await api.post('/admin/broadcasts/test', { subject, html_body: body });
      toast.success(`Test email sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  const sendBroadcast = async () => {
    try {
      setSending(true);
      setConfirmOpen(false);
      const r = await api.post('/admin/broadcasts/send', {
        subject, html_body: body, segment,
      });
      toast.success(`Broadcast queued — ${r.data.recipients_attempted} recipients`);
      setSubject(''); setBody('');
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[300px]"><Loader2 className="h-8 w-8 animate-spin text-red-400" /></div>;
  }

  const recipientCount = preview?.recipient_count ?? segments[segment] ?? 0;

  return (
    <div className="p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2" data-testid="broadcasts-title">Broadcast Email</h1>
          <p className="text-slate-300">Send one-off announcements or marketing emails to user segments.</p>
        </div>
        <Button variant="outline" onClick={refreshAll} data-testid="refresh-broadcasts">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* COMPOSE */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Compose</CardTitle>
              <CardDescription>HTML supported. Use the existing email templates as inspiration.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="subj">Subject</Label>
                <Input id="subj" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Big news from dotbox.ai" data-testid="broadcast-subject" maxLength={200} />
              </div>
              <div>
                <Label htmlFor="body">Body (HTML)</Label>
                <Textarea
                  id="body"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={12}
                  className="font-mono text-sm"
                  placeholder={'<p>Hi {{full_name}},</p>\n<p>We just shipped...</p>'}
                  data-testid="broadcast-body"
                  maxLength={100000}
                />
                <p className="text-xs text-gray-500 mt-1">{body.length} / 100,000 chars</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="seg">Recipient segment</Label>
                  <Select value={segment} onValueChange={setSegment}>
                    <SelectTrigger id="seg" data-testid="broadcast-segment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SEGMENT_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>
                          {label} ({segments[k] ?? '—'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <div className="bg-blue-950/40 border border-blue-800/50 rounded p-3 w-full text-sm">
                    <div className="flex items-center gap-2 text-blue-900">
                      <Users className="h-4 w-4" />
                      <strong data-testid="recipient-count">{previewLoading ? '…' : recipientCount}</strong>
                      <span>recipients matched</span>
                    </div>
                    {preview?.samples && preview.samples.length > 0 && (
                      <div className="text-xs text-blue-300 mt-1 truncate">
                        e.g. {preview.samples.slice(0, 3).map(s => s.email).join(', ')}
                        {preview.samples.length > 3 ? '…' : ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button variant="outline" onClick={sendTestToMyself} disabled={sending || !subject || !body} data-testid="send-test-btn">
                  <Eye className="h-4 w-4 mr-1" /> Send test to myself
                </Button>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={sending || !subject || !body || recipientCount === 0}
                  className="bg-[#DC2626] hover:bg-[#3a7ec0]"
                  data-testid="send-broadcast-btn"
                >
                  <Send className="h-4 w-4 mr-1" /> Send to {recipientCount} recipients
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* HISTORY */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Recent broadcasts</CardTitle>
              <CardDescription>Last 20 sends, newest first</CardDescription>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No broadcasts sent yet.</p>
              ) : (
                <div className="space-y-3">
                  {history.map(b => (
                    <div key={b.id} className="border rounded-lg p-3 text-sm" data-testid={`broadcast-item-${b.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <strong className="truncate flex-1">{b.subject}</strong>
                        {statusBadge(b.status)}
                      </div>
                      <div className="text-xs text-gray-500">{SEGMENT_LABELS[b.segment] || b.segment} · {fmtDate(b.started_at)}</div>
                      <div className="flex gap-3 text-xs mt-1.5">
                        <span className="text-slate-300">Attempt: <strong>{b.recipients_attempted}</strong></span>
                        <span className="text-emerald-300">✓ <strong>{b.recipients_succeeded}</strong></span>
                        {b.recipients_failed > 0 && <span className="text-amber-300">✗ <strong>{b.recipients_failed}</strong></span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CONFIRM DIALOG */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="broadcast-confirm-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-amber-500" /> Confirm broadcast</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2 text-sm">
            <p>You are about to send to <strong>{recipientCount}</strong> recipients in the <strong>{SEGMENT_LABELS[segment]}</strong> segment.</p>
            <p className="text-slate-300"><strong>Subject:</strong> {subject}</p>
            <p className="text-amber-700 text-xs">This action cannot be undone. Emails dispatch via SendGrid in the background — delivery summary will appear in the history.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</Button>
            <Button onClick={sendBroadcast} disabled={sending} className="bg-[#DC2626] hover:bg-[#3a7ec0]" data-testid="confirm-send-broadcast-btn">
              {sending ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending…</> : <><Check className="h-4 w-4 mr-1" /> Yes, send broadcast</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BroadcastsPage;
